// Small shared pieces of the studio: fields, the save bar, the studio's status
// words, and two-step buttons for things that can't be undone.
import * as React from "react"
import { Dots } from "../../toolkit/Dots"
import { EASE, enter, stagger } from "../../toolkit/motion"
import { day, fmtDate, type Summary } from "../api"

export const studioPath = (...parts: (string | undefined)[]) => "/" + ["admin", ...parts.filter(Boolean)].map((p) => encodeURIComponent(p!)).join("/")

/** a label above an input, in the mono; the input has a rule under it */
export function Field(props: { label: string; wide?: boolean; children: React.ReactNode; hint?: string }) {
    return (
        <label className={"e-field" + (props.wide ? " e-wide" : "")}>
            <span className="e-label nto-mono">{props.label}</span>
            {props.children}
            {props.hint && <span className="e-hint nto-mono">{props.hint}</span>}
        </label>
    )
}

/** a button that asks again before doing it: first click turns it into "Sure?" */
export function Confirm(props: { label: string; sure?: string; onYes: () => void; danger?: boolean; className?: string }) {
    const [asking, setAsking] = React.useState(false)
    React.useEffect(() => {
        if (!asking) return
        const t = window.setTimeout(() => setAsking(false), 4000)
        return () => window.clearTimeout(t)
    }, [asking])
    return (
        <button
            type="button"
            className={"e-btn" + (props.danger ? " e-danger" : "") + (asking ? " e-asking" : "") + (props.className ? " " + props.className : "")}
            onClick={() => (asking ? (setAsking(false), props.onYes()) : setAsking(true))}
        >
            {asking ? props.sure || `${props.label}? Click again` : props.label}
        </button>
    )
}

/** remove-a-row: the five-dot cross, turning on hover */
export function RemoveButton(props: { onClick: () => void; label: string }) {
    return (
        <button type="button" className="e-remove" onClick={props.onClick} aria-label={props.label} title={props.label}>
            <Dots shape="cross" />
        </button>
    )
}

export type SaveState = "idle" | "saving" | "saved" | { error: string }

/** the black bar at the bottom while there's something to save */
export function SaveBar(props: { dirty: boolean; state: SaveState; note?: string; onSave: () => void; onDiscard: () => void }) {
    const { dirty, note, onSave, onDiscard } = props
    // "Saved" shows for a moment, then the bar goes
    const [settled, setSettled] = React.useState(false)
    React.useEffect(() => {
        setSettled(false)
        if (props.state !== "saved") return
        const t = window.setTimeout(() => setSettled(true), 1800)
        return () => window.clearTimeout(t)
    }, [props.state])
    const state: SaveState = props.state === "saved" && settled ? "idle" : props.state
    const bar = React.useRef<HTMLDivElement>(null)
    const show = dirty || state === "saving" || state === "saved" || typeof state === "object"
    const key = typeof state === "object" ? "error" : dirty ? (state === "saving" ? "saving" : "dirty") : state
    React.useLayoutEffect(() => {
        if (!bar.current) return
        stagger(Array.from(bar.current.querySelectorAll("[data-in]")).reverse(), { from: "left", distance: 14, duration: 500, easing: EASE.out, gap: 50 })
    }, [key])
    const first = React.useRef(true)
    React.useLayoutEffect(() => {
        if (show && bar.current && first.current) enter(bar.current, { from: "up", distance: 30, duration: 700 })
        first.current = !show
    }, [show])
    if (!show) return null
    return (
        <div ref={bar} className="p-approve e-save" data-state={key}>
            {typeof state === "object" ? (
                <span className="p-bad" data-in>
                    {state.error}
                </span>
            ) : state === "saved" && !dirty ? (
                <span data-in>Saved</span>
            ) : (
                <span data-in>{state === "saving" ? "Saving…" : note || "Unsaved changes"}</span>
            )}
            {dirty && state !== "saving" && (
                <button type="button" className="p-approve-cancel" onClick={onDiscard} data-in>
                    Discard
                </button>
            )}
            {dirty && (
                <button type="button" className="p-send p-send-go" onClick={onSave} disabled={state === "saving"} data-in>
                    <span>Save</span>
                    <Dots shape="arrow" />
                </button>
            )}
        </div>
    )
}

/** where a project stands, from the studio's summary */
export function summaryStatus(p: Summary["projects"][number], now = Date.now()) {
    if (!p.approval) return { text: "Estimate to approve", tone: "act" as const }
    if (!p.start) return { text: "No planning", tone: "next" as const }
    if (now < day(p.start)) return { text: `Starts ${fmtDate(p.start)}`, tone: "next" as const }
    if (now > day(p.end) + 43200000) return { text: "Finished", tone: "done" as const }
    const cur = p.phases.find((ph) => now >= day(ph.start) && now <= day(ph.end) + 43200000)
    return { text: cur ? `${cur.name}, in progress` : "In progress", tone: "now" as const }
}

export function progressOf(p: Summary["projects"][number], now = Date.now()) {
    if (!p.start) return 0
    const s = day(p.start),
        e = day(p.end) + 43200000
    return Math.min(1, Math.max(0, (now - s) / (e - s)))
}

/** the browser asks before leaving with unsaved changes */
export function useLeaveGuard(dirty: boolean) {
    React.useEffect(() => {
        if (!dirty) return
        const f = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = ""
        }
        addEventListener("beforeunload", f)
        return () => removeEventListener("beforeunload", f)
    }, [dirty])
}

export const slugify = (s: string) =>
    s
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 40)

/** the round tick in front of a row while selecting */
export function Check({ on, part }: { on: boolean; part?: boolean }) {
    return <span className="e-check" data-on={on || undefined} data-part={part || undefined} aria-hidden="true" />
}

/** picking rows in a list: which keys are picked, and select mode on/off (Esc leaves it) */
export function useSelection() {
    const [on, setOn] = React.useState(false)
    const [picked, setPicked] = React.useState<Set<string>>(new Set())
    const stop = React.useCallback(() => (setOn(false), setPicked(new Set())), [])
    React.useEffect(() => {
        if (!on) return
        const f = (e: KeyboardEvent) => e.key === "Escape" && stop()
        addEventListener("keydown", f)
        return () => removeEventListener("keydown", f)
    }, [on, stop])
    const toggle = (k: string) =>
        setPicked((s) => {
            const n = new Set(s)
            n.has(k) ? n.delete(k) : n.add(k)
            return n
        })
    return { on, start: () => setOn(true), stop, picked, toggle, has: (k: string) => picked.has(k) }
}

/** "Select" / "Done": turns select mode on and off */
export function SelectToggle({ on, onStart, onStop }: { on: boolean; onStart: () => void; onStop: () => void }) {
    return (
        <button type="button" className="e-select-toggle nto-mono" aria-pressed={on} onClick={on ? onStop : onStart}>
            {on ? "Done" : "Select"}
        </button>
    )
}

const plural = (n: number, one: string, many = one + "s") => `${n} ${n === 1 ? one : many}`
export { plural }

/** the black bar while selecting: what's picked, then Delete, asked once more */
export function DeleteBar(props: { summary: string; question: string; count: number; onCancel: () => void; onDelete: () => Promise<boolean> }) {
    const [step, setStep] = React.useState<"idle" | "confirm" | "deleting" | "error">("idle")
    const bar = React.useRef<HTMLDivElement>(null)
    React.useEffect(() => {
        if (props.count === 0) setStep("idle")
    }, [props.count])
    React.useLayoutEffect(() => {
        if (bar.current) enter(bar.current, { from: "up", distance: 30, duration: 700 })
    }, [])
    React.useLayoutEffect(() => {
        if (!bar.current) return
        stagger(Array.from(bar.current.querySelectorAll("[data-in]")).reverse(), { from: "left", distance: 14, duration: 500, easing: EASE.out, gap: 50 })
    }, [step])
    const del = async () => {
        setStep("deleting")
        const ok = await props.onDelete()
        setStep(ok ? "idle" : "error")
    }
    return (
        <div ref={bar} className="p-approve e-save e-delete-bar" data-step={step}>
            {step === "idle" && (
                <>
                    <span data-in>{props.count ? props.summary : "Tick what to delete"}</span>
                    <button type="button" className="p-approve-cancel" onClick={props.onCancel} data-in>
                        Cancel
                    </button>
                    <button type="button" className="p-send p-send-go e-send-danger" disabled={!props.count} onClick={() => setStep("confirm")} data-in>
                        <span>Delete</span>
                        <Dots shape="cross" />
                    </button>
                </>
            )}
            {step === "confirm" && (
                <>
                    <span data-in>{props.question} This can't be undone.</span>
                    <button type="button" className="p-approve-cancel" onClick={() => setStep("idle")} data-in>
                        Not yet
                    </button>
                    <button type="button" className="p-send p-send-go e-send-danger" onClick={del} data-in>
                        <span>Yes, delete</span>
                        <Dots shape="cross" />
                    </button>
                </>
            )}
            {step === "deleting" && <span data-in>Deleting…</span>}
            {step === "error" && (
                <>
                    <span className="p-bad" data-in>
                        Couldn't delete, try again?
                    </span>
                    <button type="button" className="p-send p-send-go e-send-danger" onClick={del} data-in>
                        <span>Try again</span>
                        <Dots shape="arrow" />
                    </button>
                </>
            )}
        </div>
    )
}
